import { PickOrderLine } from './draft'

type Product = PickOrderLine['product']

export type ProductIdentity = {
	/** The part number a picker matches against the shelf. Null only if the product has none. */
	sku: string | null,
	/** Who made it. The same part number can exist under more than one maker. */
	madeBy: string | null,
	description: string,
}

/**
 * How a product reads on a pick row, a pick list or a packing slip.
 *
 * A picker matches a number, not prose, so the part number is the identity and
 * everything else supports it. `manufacturerNumber` and `partNumber` are the
 * same value (inventory-api resolves both from `part_number`); the former is
 * preferred because it is the GraphQL schema's own field.
 */
export function productIdentity( product: Product ): ProductIdentity {
	return {
		sku: product.manufacturerNumber ?? product.partNumber ?? null,
		madeBy: product.provider?.name ?? null,
		description: product.name,
	}
}

/**
 * Whether a product matches what was typed into the Pick step's search.
 *
 * Every word has to appear somewhere in the part number, manufacturer or
 * description, in any order and any case, so `baldwin filter` and `hf-65`
 * both narrow the list the way a person expects.
 */
export function matchesProductSearch( identity: ProductIdentity, query: string ): boolean {
	const words = query.trim().toLowerCase().split( /\s+/ ).filter( Boolean )
	if ( words.length === 0 ) {
		return true
	}
	const haystack = [ identity.sku, identity.madeBy, identity.description ]
		.filter( Boolean )
		.join( ' ' )
		.toLowerCase()
	return words.every( ( word ) => haystack.includes( word ) )
}
